import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { handleErrorToast } from 'src/components/molecules';
import {
  CategoryConfigurationStatus,
  GetCategoryConfigCategoriesQuery,
  useGetCategoryConfigCategoriesQuery,
  useUpdateCategoryOrdersMutation,
} from 'src/graphql/generated';

export type StatusFilterOption =
  | 'Active'
  | 'Form required'
  | 'Inactive'
  | undefined;

const STATUS_MAP: Record<string, CategoryConfigurationStatus> = {
  Active: CategoryConfigurationStatus.Active,
  'Form required': CategoryConfigurationStatus.FormRequired,
  Inactive: CategoryConfigurationStatus.Inactive,
};

const DEFAULT_ROWS_PER_PAGE = 25;
const DEFAULT_LOCALE = 'en-GB';

type Category = NonNullable<
  GetCategoryConfigCategoriesQuery['getOrganisationCategories']
>[number];

export const useCategoryTableData = (isCOI = false) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] =
    useState<StatusFilterOption>('Active');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const queryClient = useQueryClient();

  const { data, isLoading } = useGetCategoryConfigCategoriesQuery(
    { isCOI },
    { refetchOnWindowFocus: true },
  );

  const { mutate: updateOrder } = useUpdateCategoryOrdersMutation({
    onSuccess: () => {
      queryClient.refetchQueries(['GetCategoryConfigCategories']);
    },
    onError: () => {
      handleErrorToast({ toastMessage: 'Error updating category order' });
      queryClient.refetchQueries(['GetCategoryConfigCategories']);
    },
  });

  const filteredData = useMemo(() => {
    const categories = data?.getOrganisationCategories ?? [];
    let result = [...categories];

    if (statusFilter) {
      const mappedStatus = STATUS_MAP[statusFilter];
      result = result.filter((c) => c.status === mappedStatus);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((c) => {
        const name = c.locales?.[DEFAULT_LOCALE]?.name ?? '';
        const author = c.Author?.displayName ?? c.Author?.name ?? '';
        const status = c.status ?? '';
        const types: string[] = [];
        if (c.isDeterminedCategory) types.push('determined');
        if (c.isReportedCategory) types.push('reported');
        return (
          name.toLowerCase().includes(term) ||
          author.toLowerCase().includes(term) ||
          status.toLowerCase().includes(term) ||
          types.some((t) => t.includes(term))
        );
      });
    }

    return result;
  }, [data?.getOrganisationCategories, statusFilter, searchTerm]);

  const totalCount = filteredData.length;
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalCount);
  const rows = filteredData.slice(startIndex, endIndex);

  const handleStatusFilterChange = (status: StatusFilterOption) => {
    setStatusFilter((prev) => (prev === status ? undefined : status));
    setPage(1);
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const allCategories = useMemo(
    () => data?.getOrganisationCategories ?? [],
    [data?.getOrganisationCategories],
  );

  const handleReorder = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      const movedItem = filteredData[sourceIndex];
      const items = [...allCategories];

      const fromIndex = items.findIndex((c) => c.id === movedItem.id);
      items.splice(fromIndex, 1);

      const targetItem = filteredData[destinationIndex];
      const toIndex = items.findIndex((c) => c.id === targetItem.id);
      items.splice(
        destinationIndex > sourceIndex ? toIndex + 1 : toIndex,
        0,
        movedItem,
      );

      const newCategories = items.map((item: Category, index: number) => ({
        id: item.id.toString(),
        order: index,
      }));

      queryClient.setQueryData<GetCategoryConfigCategoriesQuery>(
        ['GetCategoryConfigCategories', { isCOI }],
        (old) => ({
          ...old,
          getOrganisationCategories: items.map((item, index) => ({
            ...item,
            order: index,
          })),
        }),
      );

      updateOrder({
        draggedCategoryId: movedItem.id,
        categories: newCategories,
      });
    },
    [allCategories, filteredData, updateOrder, queryClient, isCOI],
  );

  return {
    rows,
    allCategories,
    totalCount,
    isEmpty: allCategories.length === 0,
    startIndex,
    endIndex,
    page,
    rowsPerPage,
    statusFilter,
    isLoading,
    setPage,
    setRowsPerPage: handleRowsPerPageChange,
    setSearchTerm: handleSearchChange,
    setStatusFilter: handleStatusFilterChange,
    setStatusFilterDirect: setStatusFilter,
    handleReorder,
  };
};
